import { memo } from 'react';
import { Box, Typography, Button, Grid, Paper } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { keyframes } from '@mui/system';
import { Reveal } from '../useReveal';
import AnimatedNumber from '../AnimatedNumber';
import AssistantDemo from '../AssistantDemo';

const breathe = keyframes`0%,100%{transform:translate(-50%,-50%) scale(1);opacity:.85} 50%{transform:translate(-50%,-50%) scale(1.12);opacity:1}`;
const floaty  = keyframes`0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)}`;

function Hero({ colors, user, navigate }) {
  return (
    <>
        <Box id="home-hero" sx={{ 
          textAlign: 'center', 
          py: { xs: 6, md: 10 },
          position: 'relative',
        }}>
          {/* Gradient background effect */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(129,140,248,0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
            zIndex: 0,
            animation: `${breathe} 7s ease-in-out infinite`,
          }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Reveal delay={0}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <RocketLaunchIcon sx={{ color: colors.primaryLight, fontSize: 32, animation: `${floaty} 3.4s ease-in-out infinite` }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: colors.primaryLight,
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                }}
              >
                PUBLY MUSIC
              </Typography>
            </Box>
            </Reveal>

            <Reveal delay={90}>
            <Typography
              variant="h2"
              component="h1"
              sx={{ 
                fontWeight: 800,
                mb: 3,
                background: `linear-gradient(135deg, ${colors.textPrimary} 0%, ${colors.primaryLight} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              Déclarer à la SACEM
              <br />
              n'a jamais été aussi simple
            </Typography>
            </Reveal>

            <Reveal delay={180}>
            <Typography
              variant="h6"
              component="p"
              sx={{ 
                color: colors.textSecondary,
                mb: 5,
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '1rem', md: '1.25rem' },
              }}
            >
              Vous créez la musique, on s'occupe de la paperasse. Publy guide les
              artistes pas à pas pour déposer leurs œuvres à la SACEM — sans éditeur,
              sans intermédiaire, en gardant le contrôle total de vos droits.
            </Typography>
            </Reveal>

            <Reveal delay={260}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(user ? '/documents' : '/register')}
                sx={{
                  bgcolor: colors.primaryLight,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: colors.primaryLight,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(129,140,248,0.4)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {user ? 'Mes déclarations' : 'Commencer gratuitement'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(user ? '/dashboard' : '/login')}
                sx={{
                  borderColor: colors.textMuted,
                  color: colors.textPrimary,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: colors.primaryLight,
                    bgcolor: 'rgba(129,140,248,0.1)',
                  },
                }}
              >
                {user ? 'Tableau de bord' : 'Se connecter'}
              </Button>
            </Box>
            </Reveal>

            {!user && (
              <Reveal delay={340}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mt: 3, px: 2, py: 1, borderRadius: 999, bgcolor: colors.successBg, border: `1px solid ${colors.success}` }}>
                <CardGiftcardIcon sx={{ color: colors.success, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: colors.success, fontWeight: 600 }}>
                  1 crédit offert pour tester la plateforme
                </Typography>
              </Box>
              </Reveal>
            )}
          </Box>
        </Box>

        {/* Assistant demo */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: { xs: 4, md: 6 } }}>
          <Reveal delay={120} sx={{ width: '100%', maxWidth: 520, textAlign: 'center' }}>
            <Typography variant="overline" sx={{ color: colors.primaryLight, fontWeight: 700, letterSpacing: '0.08em' }}>
              L'assistant en action
            </Typography>
            <Box sx={{ mt: 1.5 }}><AssistantDemo /></Box>
          </Reveal>
        </Box>

        <Box sx={{ pb: { xs: 4, md: 6 } }}>
          <Grid container spacing={2}>
            {[
              { num: 1.5, dec: 1, suffix: ' M€+', label: 'redistribués chaque an par la SACEM' },
              { num: 8, dec: 0, suffix: ' min', label: 'pour préparer une déclaration complète' },
              { num: 100, dec: 0, suffix: '%', label: 'de contrôle conservé sur vos droits' },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Reveal delay={i * 80} sx={{ height: '100%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: { xs: 2.5, md: 3 },
                    textAlign: 'center',
                    bgcolor: colors.bgPaper,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 3,
                  }}
                >
                  <Typography variant="h4" sx={{ color: colors.primaryLight, fontWeight: 800, mb: 0.5 }}>
                    <AnimatedNumber target={item.num} decimals={item.dec} startOnView
                      format={(n) => n.toLocaleString('fr-FR', { minimumFractionDigits: item.dec })} />
                    {item.suffix}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textSecondary, lineHeight: 1.6 }}>
                    {item.label}
                  </Typography>
                </Paper>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Box>
    </>
  );
}

export default memo(Hero);
